import {
  FeatureName,
  PermissionValues,
  useAccessTokenReceived,
  useCurrentAccount,
  useCurrentWorkspace,
  useGrippContext,
  useImpersonateWorkspace,
  useInvalidateConfig,
  useIsFeatureEnabled,
  useStorageKeys,
} from '@gripp/shared-logic';
import { Colors } from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Button } from 'react-native-paper';
import { Header } from './header';
import { ProfileMenu } from './profileMenu';

export const Banner = () => {
  const isCropCircleEnabled = useIsFeatureEnabled(FeatureName.CropCircleRedesign);
  const { account } = useCurrentAccount();
  const workspace = useCurrentWorkspace(true);
  const { secureStorageHandler } = useGrippContext();
  const storageKeys = useStorageKeys();
  const [isInWorkspace, setIsInWorkspace] = useState<boolean | undefined>();
  const invalidateConfig = useInvalidateConfig();
  const [isInImpersonateUser, setIsInImpersonateUser] = useState<
    boolean | undefined
  >();
  const queryClient = useQueryClient();
  const { impersonateWorkspace } = useImpersonateWorkspace();
  const onAccessTokenReceived = useAccessTokenReceived();

  useEffect(() => {
    if (!account) {
      return;
    }
    setIsInWorkspace(
      workspace && account.hasPermission(PermissionValues.globalAdmin)
    );
    secureStorageHandler.getItem(storageKeys.oldAccessToken).then((result) => {
      setIsInImpersonateUser(result ? true : false);
    });
  }, [account, workspace]);

  const onLeaveWorkspace = async () => {
    await impersonateWorkspace(undefined);
    window.location.reload();
  };

  const onLeaveImpersonateUser = async () => {
    await onAccessTokenReceived('', true, false);
    await invalidateConfig();
    await queryClient.invalidateQueries({
      queryKey: ['currentAccount'],
    });
    window.location.reload();
  };

  return (
    <>
      {isInWorkspace && (
        <View style={styles.workspaceBanner}>
          <Text style={styles.workspaceBannerText}>
            You are acting as "Gripp Admin" in {workspace?.name} workspace
          </Text>
          <Button
            style={styles.button}
            labelStyle={{ color: Colors.white }}
            onPress={onLeaveWorkspace}
            mode="outlined"
          >
            Leave
          </Button>
        </View>
      )}
      {isInImpersonateUser && (
        <View style={styles.workspaceBanner}>
          <Text style={styles.workspaceBannerText}>
            You are acting as
            {account?.email
              ? account.email
              : account?.name?.first || account?.name?.last
              ? `${account.name.first} ${account.name.last}`
              : null}
          </Text>
          <Button
            style={styles.button}
            labelStyle={{ color: Colors.white }}
            onPress={onLeaveImpersonateUser}
            mode="outlined"
          >
            Leave
          </Button>
        </View>
      )}
      {
        !isCropCircleEnabled && (
          <View style={styles.container}>
            <Header />
            <ProfileMenu />
          </View>
        )
      }
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    height: 84,
    backgroundColor: Colors.white,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 40,
    paddingVertical: 20,
  },
  workspaceBanner: {
    flexDirection: 'row',
    height: 50,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.blue,
  },
  workspaceBannerText: {
    textAlign: 'center',
    color: Colors.white,
    fontSize: 20,
  },
  button: {
    borderColor: Colors.white,
    borderWidth: 1,
    borderRadius: 6,
    marginHorizontal: 10,
    maxHeight: 40,
  },
});
